/**
 * Command Center
 *
 * The AI-first conversational interface for FacilityAI.
 * This is the "revolutionary" UI — a single, clean screen where users
 * interact with the system through natural language and asset scanning.
 *
 * Features:
 * - Full-screen conversational chat with the FacilityAI AI
 * - Quick action chips (Scan Asset, View Dashboard, etc.)
 * - Rich response cards inline in chat (asset cards, location cards, health summaries)
 * - "Switch to Deep Data" toggle to access the traditional detailed UI
 * - Greeting with user's name and organization context
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button, message, Tooltip } from "antd";
import {
	SendOutlined,
	ScanOutlined,
	EnvironmentOutlined,
	DatabaseOutlined,
	HistoryOutlined,
	BulbOutlined,
	UserOutlined,
	AimOutlined,
	CameraOutlined,
} from "@ant-design/icons";
import { GiBrain } from "react-icons/gi";
import {
	sendChatMessage,
	getChatSuggestions,
	type ChatMessage,
} from "@/services/chat-service";
import { useUserStore } from "@/stores/user-store";
import { useUIModeStore } from "@/stores/ui-mode-store";
import { useSoulStore } from "@/stores/soul-store";
import { getUserSoul, trackUsage } from "@/services/soul-service";
import { getDashboardData } from "@/services/asset-service";
import { Copyright } from "@/layouts/signed-in/Copyright/Copyright";
import { GoogleHeatmap } from "@/views/SignedIn/Dashboard/google-heat-map";
import type { LocationAggregate } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import { type ChatCardData } from "./ChatCards";
import { ResultsPanel } from "./ResultsPanel";
import { AssetUpload, type AssetUploadResult } from "./AssetUpload";
import { SoulOnboarding } from "./SoulOnboarding";
import { SoulProfile } from "./SoulProfile";
import { LocationWorkspace } from "./LocationWorkspace";
import logo from "@assets/img/FacilityAI_Logo.svg";
import "./CommandCenter.scss";

/** Extends ChatMessage to carry optional rich card data for assistant responses */
interface DisplayMessage extends ChatMessage {
	cards?: ChatCardData[];
}

// Follow-up suggestions based on context
const FOLLOW_UP_SUGGESTIONS: Record<string, string[]> = {
	assets_attention: [
		"Show me more details",
		"What's the replacement cost?",
		"Which locations have issues?",
	],
	health_score: [
		"How can I improve this?",
		"Which assets are affected?",
		"Show trending data",
	],
	locations: [
		"What assets are here?",
		"Show health history",
		"Any recent maintenance?",
	],
	budget: [
		"Break down by type",
		"Most expensive items?",
		"What's urgent?",
	],
	general: [
		"Tell me more",
		"What should I focus on?",
		"Show related data",
	],
};

// Persona-specific subtitles
const PERSONA_SUBTITLES: Record<string, string> = {
	executive: "Here's your executive overview for today",
	finance: "Let's look at costs and budgets",
	analyst: "Ready to dive into the data",
	facility_manager: "Let's keep your facilities running smoothly",
	general_manager: "Here's what's happening across your locations",
	location_worker: "What equipment do you need help with?",
};

const CommandCenter = () => {
	const navigate = useNavigate();
	const { user } = useUserStore();
	const { setMode } = useUIModeStore();
	const { soul, hasFetched, setSoul, setLoading: setSoulLoading } = useSoulStore();

	// Chat state
	const [messages, setMessages] = useState<DisplayMessage[]>([]);
	const [inputValue, setInputValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
	const [lastQuestionType, setLastQuestionType] = useState<string>("general");
	const [showOnboarding, setShowOnboarding] = useState(false);
	const [showSoulProfile, setShowSoulProfile] = useState(false);
	const [showAssetUpload, setShowAssetUpload] = useState(false);
	const [resultCards, setResultCards] = useState<ChatCardData[]>([]);
	const [mapLocations, setMapLocations] = useState<LocationAggregate[]>([]);

	// Location workspace state
	const [focusedLocation, setFocusedLocation] = useState<LocationAggregate | null>(null);
	const [nearbyLocation, setNearbyLocation] = useState<LocationAggregate | null>(null);
	const [showProximityPrompt, setShowProximityPrompt] = useState(false);
	const [showLocationSearch, setShowLocationSearch] = useState(false);
	const [locationSearchQuery, setLocationSearchQuery] = useState("");

	// Refs
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Derived
	const hasMessages = messages.length > 0;
	const userName = user?.name?.split(" ")[0] || "there";
	const orgName = user?.organization?.fullName || user?.organization?.full_name || "";

	// Fetch location data for map on mount
	useEffect(() => {
		const fetchMapData = async () => {
			try {
				const data = await getDashboardData();
				const processed = (data.locations || []).map((loc: any) => ({
					...loc,
					health: {
						// API returns camelCase (healthScore.healthScore) after convertKeysToCamelCase
						healthScore:
							loc.healthScore?.healthScore ??
							loc.health_score?.health_score ??
							0,
						locationId: loc.id,
					},
				}));
				setMapLocations(processed);
			} catch (err) {
				console.error("Error fetching map data:", err);
			}
		};
		fetchMapData();
	}, []);

	// Haversine distance (meters) between two GPS coordinates
	const haversineDistance = useCallback(
		(lat1: number, lon1: number, lat2: number, lon2: number): number => {
			const R = 6371e3;
			const φ1 = (lat1 * Math.PI) / 180;
			const φ2 = (lat2 * Math.PI) / 180;
			const Δφ = ((lat2 - lat1) * Math.PI) / 180;
			const Δλ = ((lon2 - lon1) * Math.PI) / 180;
			const a =
				Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
				Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
			return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		},
		[],
	);

	// Restore cached location OR run geolocation proximity detection
	useEffect(() => {
		if (!mapLocations.length || focusedLocation) return;

		// Check sessionStorage for a previously focused location
		const cachedLocId = sessionStorage.getItem("cc_focused_location_id");
		if (cachedLocId) {
			const cached = mapLocations.find((loc) => String(loc.id) === cachedLocId);
			if (cached) {
				setFocusedLocation(cached);
				trackUsage("feature_usage", "location_workspace_restored");
				return; // Skip geolocation — we already have a cached location
			}
			// Cached ID no longer valid, clear it
			sessionStorage.removeItem("cc_focused_location_id");
		}

		// No cached location — run geolocation to detect proximity
		if (!navigator.geolocation) return;

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords;
				let closest: LocationAggregate | null = null;
				let closestDist = Infinity;

				for (const loc of mapLocations) {
					if (
						typeof loc.latitude !== "number" ||
						typeof loc.longitude !== "number"
					)
						continue;
					const dist = haversineDistance(latitude, longitude, loc.latitude, loc.longitude);
					if (dist < closestDist) {
						closestDist = dist;
						closest = loc;
					}
				}

				if (closest && closestDist <= 500) {
					setNearbyLocation(closest);
					setShowProximityPrompt(true);
					trackUsage("feature_usage", "geo_proximity_detected");
				}
			},
			(err) => {
				console.warn("Geolocation unavailable:", err.message);
			},
			{ enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
		);
	}, [mapLocations, focusedLocation, haversineDistance]);

	// Filtered locations for manual search — matches name, address, city, state, store #, zip
	const filteredLocations = useMemo(() => {
		if (!locationSearchQuery.trim()) return mapLocations;
		const q = locationSearchQuery.toLowerCase();
		return mapLocations.filter((loc: any) => {
			return (
				(loc.name || "").toLowerCase().includes(q) ||
				(loc.address1 || "").toLowerCase().includes(q) ||
				(loc.city || "").toLowerCase().includes(q) ||
				(loc.state || "").toLowerCase().includes(q) ||
				(loc.storeNumber || loc.store_number || "").toLowerCase().includes(q) ||
				(loc.zip || "").toLowerCase().includes(q)
			);
		});
	}, [mapLocations, locationSearchQuery]);

	// Handle focus on a location — also caches to sessionStorage
	const handleFocusLocation = useCallback(
		(location: LocationAggregate) => {
			setFocusedLocation(location);
			setShowProximityPrompt(false);
			setShowLocationSearch(false);
			setLocationSearchQuery("");
			sessionStorage.setItem("cc_focused_location_id", String(location.id));
			trackUsage("feature_usage", "location_workspace_activated");
		},
		[],
	);

	// Exit location workspace — clears cache so geolocation can re-run
	const handleExitWorkspace = useCallback(() => {
		setFocusedLocation(null);
		sessionStorage.removeItem("cc_focused_location_id");
	}, []);

	// Fetch SOUL on mount
	useEffect(() => {
		const fetchSoul = async () => {
			if (hasFetched && soul?.onboardingCompleted) return;
			setSoulLoading(true);
			try {
				const fetchedSoul = await getUserSoul();
				setSoul(fetchedSoul);
				if (!fetchedSoul || !fetchedSoul.onboardingCompleted) {
					setShowOnboarding(true);
				}
			} catch (error) {
				console.error("Error fetching soul:", error);
			} finally {
				setSoulLoading(false);
			}
		};
		fetchSoul();
	}, []);

	// Personalized subtitle
	const welcomeSubtitle = useMemo(() => {
		if (soul?.onboardingCompleted && soul.persona) {
			const personalizedText = PERSONA_SUBTITLES[soul.persona];
			if (personalizedText) return personalizedText;
		}
		return `How can I help you manage ${orgName ? `${orgName}'s` : "your"} facilities today?`;
	}, [soul, orgName]);

	// Get current hour for greeting
	const greeting = useMemo(() => {
		const hour = new Date().getHours();
		if (hour < 12) return "Good morning";
		if (hour < 17) return "Good afternoon";
		return "Good evening";
	}, []);

	// Parse navigation command from AI response
	const parseNavigationCommand = (content: string): { cleanContent: string; navPath: string | null } => {
		const navRegex = /\[NAV:(\/[^\]]*)\]/;
		const match = content.match(navRegex);
		if (match) {
			return { cleanContent: content.replace(navRegex, "").trim(), navPath: match[1] };
		}
		return { cleanContent: content, navPath: null };
	};

	// Handle navigation
	const handleNavigation = useCallback((path: string) => {
		// Track navigation silently
		trackUsage("page_visit", path);
		// Switch to deep data mode for detailed views
		setMode("deep-data");
		navigate(path);
	}, [navigate, setMode]);

	// Detect question type for follow-ups
	const detectQuestionType = (question: string, response: string): string => {
		const q = question.toLowerCase();
		const r = response.toLowerCase();
		if (q.includes("attention") || q.includes("poor") || q.includes("condition") || r.includes("poor condition")) return "assets_attention";
		if (q.includes("health") || q.includes("score") || r.includes("health score")) return "health_score";
		if (q.includes("location") || q.includes("site") || r.includes("location")) return "locations";
		if (q.includes("budget") || q.includes("cost") || q.includes("spend") || r.includes("budget")) return "budget";
		return "general";
	};

	// Follow-up suggestions
	const followUpSuggestions = useMemo(() => {
		return FOLLOW_UP_SUGGESTIONS[lastQuestionType] || FOLLOW_UP_SUGGESTIONS.general;
	}, [lastQuestionType]);

	// Scroll to bottom
	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [messages, scrollToBottom]);

	// Fetch suggestions on mount
	useEffect(() => {
		const fetchSuggestions = async () => {
			const suggestedQuestions = await getChatSuggestions();
			setSuggestions(suggestedQuestions);
		};
		fetchSuggestions();
	}, []);

	// Focus input on mount
	useEffect(() => {
		setTimeout(() => {
			inputRef.current?.focus();
		}, 500);
	}, []);

	// Build chat messages with optional location context
	const buildChatPayload = useCallback(
		(msgs: DisplayMessage[]): ChatMessage[] => {
			if (!focusedLocation) return msgs;
			// Prepend location context as a user→assistant exchange so the AI knows
			// which location the user is focused on without needing system role support
			const locName = focusedLocation.name || focusedLocation.address1 || "this location";
			const locAddr = [focusedLocation.address1, focusedLocation.city, focusedLocation.state, focusedLocation.zip].filter(Boolean).join(", ");
			const ctxUser: ChatMessage = {
				role: "user",
				content: `[Context] I am currently at location "${locName}" (ID: ${focusedLocation.id}, Address: ${locAddr}, Health Score: ${focusedLocation.health?.healthScore ?? 0}%). Please keep this in mind for my questions.`,
			};
			const ctxAssistant: ChatMessage = {
				role: "assistant",
				content: `Got it! I'll focus on ${locName}. What would you like to know about this location?`,
			};
			return [ctxUser, ctxAssistant, ...msgs];
		},
		[focusedLocation],
	);

	// Send message
	const handleSendMessage = async (messageText?: string) => {
		const text = messageText || inputValue.trim();
		if (!text || isLoading) return;

		setPendingNavigation(null);
		const userMessage: DisplayMessage = { role: "user", content: text };
		const newMessages = [...messages, userMessage];
		setMessages(newMessages);
		setInputValue("");
		setIsLoading(true);

		try {
			const result = await sendChatMessage(buildChatPayload(newMessages));
			const { cleanContent, navPath } = parseNavigationCommand(result.text);
			if (navPath) setPendingNavigation(navPath);
			const qType = detectQuestionType(text, cleanContent);
			setLastQuestionType(qType);
			const hasCards = result.cards && result.cards.length > 0;
			setMessages([...newMessages, {
				role: "assistant",
				content: cleanContent,
				cards: hasCards ? result.cards : undefined,
			}]);
			// Update results panel cards — keep previous cards if new response has none
			if (hasCards) {
				setResultCards(result.cards);
			}
			// Track the query topic silently
			trackUsage("query_topic", qType);
		} catch (error: any) {
			console.error("Chat error:", error);
			message.error(error.message || "Failed to get response");
			setMessages(messages);
		} finally {
			setIsLoading(false);
		}
	};

	// Key press
	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	// New conversation
	const handleNewConversation = () => {
		setMessages([]);
		setPendingNavigation(null);
		setLastQuestionType("general");
		setResultCards([]);
		inputRef.current?.focus();
	};

	// Linkify location references
	const linkifyReferences = (text: string): React.ReactNode[] => {
		const parts: React.ReactNode[] = [];
		const locationPattern = /(?:at\s+)?location\s+(\d+)/gi;
		let lastIndex = 0;
		let match;

		while ((match = locationPattern.exec(text)) !== null) {
			if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
			parts.push(
				<button
					key={`loc-${match.index}`}
					type="button"
					className="cc-inline-link"
					onClick={() => handleNavigation(`/locations/${match![1]}`)}
				>
					<EnvironmentOutlined /> {match[0]}
				</button>
			);
			lastIndex = match.index + match[0].length;
		}

		if (lastIndex < text.length) parts.push(text.slice(lastIndex));
		return parts.length > 0 ? parts : [text];
	};

	// Format message
	const formatMessage = (content: string) => {
		return content.split("\n").map((line, i) => {
			const trimmed = line.trim();
			const processLine = (text: string): React.ReactNode => <>{linkifyReferences(text)}</>;

			if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
				return <li key={i} className="cc-list-item">{processLine(trimmed.substring(2))}</li>;
			}
			if (trimmed.match(/^\d+\.\s/)) {
				return <li key={i} className="cc-list-item">{processLine(trimmed.replace(/^\d+\.\s/, ""))}</li>;
			}
			if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
				return <strong key={i} className="cc-bold">{processLine(trimmed.slice(2, -2))}</strong>;
			}
			return (
				<span key={i}>
					{processLine(line)}
					{i < content.split("\n").length - 1 && <br />}
				</span>
			);
		});
	};

	// Handle onboarding completion
	const handleOnboardingComplete = () => {
		setShowOnboarding(false);
	};

	// Reset SOUL — re-enter onboarding flow
	const handleSoulReset = () => {
		setShowOnboarding(true);
	};

	// Navigate to slim-form capture to add a new asset
	const handleAddNewAsset = useCallback(() => {
		trackUsage("feature_usage", "add_new_asset_from_scan");
		setMode("deep-data");
		navigate("/slim-form/capture", { state: { from: "/command-center" } });
	}, [navigate, setMode]);

	// Mobile hero CTA: jump straight to SlimForm capture with optional location pre-selection
	const handleCollectAsset = useCallback(() => {
		trackUsage("feature_usage", "collect_asset_mobile_cta");
		if (focusedLocation) {
			// Skip location step — pass location ID and name
			navigate("/slim-form/capture", {
				state: {
					from: "/command-center",
					preselectedLocationId: focusedLocation.id,
					preselectedLocationName: focusedLocation.name || focusedLocation.address1 || "",
				},
			});
		} else {
			// No location focused — start from step 0
			navigate("/slim-form/capture", { state: { from: "/command-center" } });
		}
	}, [navigate, focusedLocation]);

	// Handle inline asset recognition result
	const handleAssetUploadResult = async (result: AssetUploadResult) => {
		setShowAssetUpload(false);
		trackUsage("feature_usage", "inline_scan");

		// Inject the scan summary as a user message and get AI response
		const userMessage: DisplayMessage = { role: "user", content: result.chatMessage };
		const newMessages = [...messages, userMessage];
		setMessages(newMessages);
		setIsLoading(true);

		// Build asset card data from recognition results for the results panel
		if (result.recognition.matches.length > 0) {
			const assetCards: ChatCardData[] = result.recognition.matches.slice(0, 3).map((match) => ({
				type: "asset" as const,
				assetTag: match.asset.asset_tag || match.asset.assetTag || "Unknown",
				assetType: match.asset.asset_type_name || match.asset.assetType || "Equipment",
				condition: match.asset.condition || match.asset.condition_name || "Unknown",
				location: match.asset.location_name || match.asset.locationName || "",
				confidence: Math.round(match.confidence * 100),
			}));
			setResultCards(assetCards);
		}

		try {
			const chatResult = await sendChatMessage(buildChatPayload(newMessages));
			const { cleanContent, navPath } = parseNavigationCommand(chatResult.text);
			if (navPath) setPendingNavigation(navPath);
			setLastQuestionType("assets_attention");
			const hasCards = chatResult.cards && chatResult.cards.length > 0;
			setMessages([...newMessages, {
				role: "assistant",
				content: cleanContent,
				cards: hasCards ? chatResult.cards : undefined,
			}]);
			if (hasCards) {
				setResultCards(chatResult.cards);
			}
			trackUsage("query_topic", "asset_scan");
		} catch (error: any) {
			console.error("Chat error after scan:", error);
			message.error(error.message || "Failed to get AI response");
			setMessages(messages);
		} finally {
			setIsLoading(false);
		}
	};

	// Show onboarding if needed
	if (showOnboarding) {
		return (
			<div className={`command-center`}>
				<header className="cc-header">
					<div className="cc-header-left" />
					<div className="cc-header-right">
						<Tooltip title="Switch to traditional detailed UI">
							<Button
								type="text"
								icon={<DatabaseOutlined />}
								onClick={() => setMode("deep-data")}
								className="cc-header-btn cc-deep-data-btn"
							>
								Deep Data
							</Button>
						</Tooltip>
					</div>
				</header>
				<main className="cc-main">
					<SoulOnboarding userName={userName} onComplete={handleOnboardingComplete} />
				</main>
				<div className="cc-copyright">
					<Copyright />
				</div>
			</div>
		);
	}

	return (
		<div className={`command-center ${hasMessages ? "has-messages" : ""}`}>

			{/* Top bar */}
			<header className="cc-header">
				<div className="cc-header-left" />
				<div className="cc-header-right">
					{hasMessages && (
						<Tooltip title="New conversation">
							<Button
								type="text"
								icon={<HistoryOutlined />}
								onClick={handleNewConversation}
								className="cc-header-btn"
							>
								New Chat
							</Button>
						</Tooltip>
					)}
					<Tooltip title="Your AI archetype and preferences">
						<Button
							type="text"
							icon={<UserOutlined />}
							onClick={() => setShowSoulProfile(true)}
							className="cc-header-btn"
						>
							Archetype
						</Button>
					</Tooltip>
					<Tooltip title="Switch to traditional detailed UI">
						<Button
							type="text"
							icon={<DatabaseOutlined />}
							onClick={() => setMode("deep-data")}
							className="cc-header-btn cc-deep-data-btn"
						>
							Deep Data
						</Button>
					</Tooltip>
				</div>
			</header>

			{/* Main content area */}
			<main className="cc-main">
				{/* Welcome section — shown when no messages */}
				{!hasMessages && (
					<div className="cc-welcome">
						<div className="cc-welcome-logo">
							<img src={logo} alt="FacilityAI" className="cc-logo-img" />
							{/* Pulse animation — centered on logo, behind everything */}
							<div className="cc-pulse-wrapper">
								<div className="cc-pulse-container">
									<div className="cc-pulse-core" />
									<div className="cc-pulse-ring cc-pulse-ring-1" />
									<div className="cc-pulse-ring cc-pulse-ring-2" />
									<div className="cc-pulse-ring cc-pulse-ring-3" />
									<div className="cc-pulse-ring cc-pulse-ring-4" />
									<div className="cc-pulse-ring cc-pulse-ring-5" />
									<div className="cc-pulse-glow" />
								</div>
							</div>
						</div>

						<h1 className="cc-welcome-title">
							{greeting}, {userName}
						</h1>
						<p className="cc-welcome-subtitle">
							{welcomeSubtitle}
						</p>

						{/* Proximity prompt banner */}
						{showProximityPrompt && nearbyLocation && (
							<div className="cc-proximity-prompt">
								<div className="cc-proximity-icon">
									<AimOutlined />
								</div>
								<div className="cc-proximity-text">
									<span className="cc-proximity-label">It looks like you're at</span>
									<strong className="cc-proximity-name">
										{nearbyLocation.name || nearbyLocation.address1 || "this location"}
									</strong>
								</div>
								<div className="cc-proximity-actions">
									<Button
										type="primary"
										size="small"
										onClick={() => handleFocusLocation(nearbyLocation)}
									>
										Yes, focus here
									</Button>
									<Button
										type="text"
										size="small"
										onClick={() => setShowProximityPrompt(false)}
									>
										No thanks
									</Button>
								</div>
							</div>
						)}

						{/* Location workspace OR heat map */}
						{focusedLocation ? (
							<LocationWorkspace
								location={focusedLocation}
								onExit={handleExitWorkspace}
								onScanAsset={() => setShowAssetUpload(true)}
								onNavigate={handleNavigation}
								onCollectAsset={handleCollectAsset}
							/>
						) : mapLocations.length > 0 ? (
							<>
								{/* Desktop: map card with set location */}
								<div className="cc-map-card">
									<div className="cc-map-inner">
										<GoogleHeatmap locations={mapLocations} />
									</div>
									<div className="cc-map-label">
										<EnvironmentOutlined />
										<span>{mapLocations.length} Locations</span>
									</div>
									<button
										type="button"
										className={`cc-set-location-btn ${!nearbyLocation ? "cc-set-location-glow" : ""}`}
										onClick={() => setShowLocationSearch(true)}
									>
										<AimOutlined />
										<span>Set Location</span>
										{!nearbyLocation && (
											<span className="cc-set-location-hint">
												Set your location to sharpen your focus
											</span>
										)}
									</button>
								</div>

								{/* Mobile: prominent Set Location CTA (replaces map) */}
								<button
									type="button"
									className="cc-mobile-set-location"
									onClick={() => setShowLocationSearch(true)}
								>
									<div className="cc-mobile-set-icon">
										<AimOutlined />
									</div>
									<div className="cc-mobile-set-text">
										<strong>Set Your Location</strong>
										<span>Tap to select where you are and sharpen your focus</span>
									</div>
								</button>

								{/* Mobile: Collect Asset CTA */}
								<button
									type="button"
									className="cc-mobile-collect-asset"
									onClick={handleCollectAsset}
								>
									<div className="cc-mobile-collect-icon">
										<CameraOutlined />
									</div>
									<div className="cc-mobile-collect-text">
										<strong>Collect New Asset</strong>
										<span>Capture photos to add an asset to your portfolio</span>
									</div>
								</button>
							</>
						) : null}

						{/* Location search modal */}
						{showLocationSearch && (
							<div className="cc-location-search-overlay" onClick={() => setShowLocationSearch(false)}>
								<div className="cc-location-search-modal" onClick={(e) => e.stopPropagation()}>
									<div className="cc-loc-search-header">
										<h3>Select a Location</h3>
										<button
											type="button"
											className="cc-loc-close-btn"
											onClick={() => setShowLocationSearch(false)}
										>
											&times;
										</button>
									</div>
									<div className="cc-loc-search-bar">
										<Input
											autoFocus
											placeholder="Search name, address, store #, city, state..."
											value={locationSearchQuery}
											onChange={(e) => setLocationSearchQuery(e.target.value)}
											prefix={<EnvironmentOutlined />}
											allowClear
											size="large"
										/>
										<span className="cc-loc-result-count">
											{filteredLocations.length} location{filteredLocations.length !== 1 ? "s" : ""}
										</span>
									</div>
									{/* Table header */}
									<div className="cc-loc-table-header">
										<span className="cc-loc-col cc-loc-col-addr">Address</span>
										<span className="cc-loc-col cc-loc-col-store">Store #</span>
										<span className="cc-loc-col cc-loc-col-city">City</span>
										<span className="cc-loc-col cc-loc-col-state">State</span>
										<span className="cc-loc-col cc-loc-col-health">Health</span>
										<span className="cc-loc-col cc-loc-col-assets">Assets</span>
									</div>
									{/* Results list */}
									<div className="cc-loc-search-results">
										{filteredLocations.map((loc: any) => {
											const hs = loc.health?.healthScore ?? 0;
											const hsColor = hs >= 75 ? "#3f6e01" : hs >= 25 ? "#e0a724" : "#bf1e2d";
											const storeNum = loc.storeNumber || loc.store_number || "";
											const assetCount = loc.submissionCount ?? loc.submission_count ?? 0;
											return (
												<button
													key={loc.id}
													type="button"
													className="cc-loc-search-row"
													onClick={() => handleFocusLocation(loc)}
												>
													<span
														className="cc-loc-row-indicator"
														style={{ backgroundColor: hsColor }}
													/>
													<span className="cc-loc-col cc-loc-col-addr">
														<span className="cc-loc-primary">
															{loc.name || loc.address1 || "—"}
														</span>
														{loc.name && loc.address1 && (
															<span className="cc-loc-secondary">{loc.address1}</span>
														)}
													</span>
													<span className="cc-loc-col cc-loc-col-store">
														{storeNum || "—"}
													</span>
													<span className="cc-loc-col cc-loc-col-city">
														{loc.city || "—"}
													</span>
													<span className="cc-loc-col cc-loc-col-state">
														{loc.state || "—"}
													</span>
													<span className="cc-loc-col cc-loc-col-health">
														<span
															className="cc-loc-health-pill"
															style={{ color: hsColor }}
														>
															{hs}%
														</span>
													</span>
													<span className="cc-loc-col cc-loc-col-assets">
														{assetCount}
													</span>
												</button>
											);
										})}
										{filteredLocations.length === 0 && (
											<div className="cc-loc-search-empty">
												No locations match your search
											</div>
										)}
									</div>
								</div>
							</div>
						)}

					</div>
				)}

				{/* Engaged split layout — shown when conversation is active */}
				{hasMessages && (
					<div className="cc-engaged">
						{/* Left panel — Conversation */}
						<div className="cc-conversation-panel">
							{/* Mini logo */}
							<div className="cc-conv-header">
								<img src={logo} alt="FacilityAI" className="cc-mini-logo" />
							</div>

							{/* Input at top — always accessible, with scan action */}
							<div className="cc-conv-input">
								<div className="cc-conv-input-row">
									<Input
										ref={inputRef as any}
										value={inputValue}
										onChange={(e) => setInputValue(e.target.value)}
										onKeyPress={handleKeyPress}
										placeholder="Ask a follow-up..."
										disabled={isLoading}
										className="cc-input"
										size="large"
										suffix={
											<Button
												type="primary"
												icon={<SendOutlined />}
												onClick={() => handleSendMessage()}
												disabled={!inputValue.trim() || isLoading}
												className="cc-send-btn"
												shape="circle"
											/>
										}
									/>
								<Tooltip title="Scan Asset">
									<button
										type="button"
										className="cc-scan-strip-btn"
										onClick={() => setShowAssetUpload(true)}
									>
										<ScanOutlined />
									</button>
								</Tooltip>
								</div>
							</div>

							{/* Scrollable messages */}
							<div className="cc-conv-messages">
								{messages.map((msg, index) => (
									<div
										key={index}
										className={`cc-message ${msg.role === "user" ? "cc-message-user" : "cc-message-assistant"}`}
									>
										{msg.role === "assistant" && (
											<div className="cc-message-avatar">
												<GiBrain />
											</div>
										)}
										<div className="cc-message-bubble">
											<div className="cc-message-content">
												{formatMessage(msg.content)}
											</div>
										</div>
									</div>
								))}

								{/* Loading indicator */}
								{isLoading && (
									<div className="cc-message cc-message-assistant">
										<div className="cc-message-avatar">
											<GiBrain />
										</div>
										<div className="cc-message-bubble">
											<div className="cc-message-content cc-thinking">
												<div className="cc-thinking-dots">
													<span />
													<span />
													<span />
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Follow-up suggestions */}
								{!isLoading && messages.length > 0 && messages[messages.length - 1].role === "assistant" && (
									<div className="cc-follow-ups">
										{followUpSuggestions.map((suggestion, index) => (
											<button
												key={index}
												type="button"
												className="cc-follow-up-chip"
												onClick={() => handleSendMessage(suggestion)}
											>
												{suggestion}
											</button>
										))}
									</div>
								)}

								<div ref={messagesEndRef} />
							</div>
						</div>

						{/* Right panel — Results/Insights */}
						<div className="cc-results-panel-wrapper">
							<ResultsPanel
								cards={resultCards}
								pendingNavigation={pendingNavigation}
								onNavigate={handleNavigation}
								isLoading={isLoading}
							/>
						</div>
					</div>
				)}
			</main>

			{/* Floating bottom bar — always visible */}
			{!hasMessages && (
				<div className="cc-bottom-bar">
					{/* AI suggestion chips */}
					{suggestions.length > 0 && (
						<div className="cc-suggestions">
							{suggestions.map((suggestion, index) => (
								<button
									key={index}
									type="button"
									className="cc-suggestion-chip"
									onClick={() => handleSendMessage(suggestion)}
								>
									<BulbOutlined />
									<span>{suggestion}</span>
								</button>
							))}
						</div>
					)}

					{/* Chat input + Scan Asset */}
					<div className="cc-action-row">
						<div className="cc-welcome-input">
							<Input
								ref={inputRef as any}
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
								onKeyPress={handleKeyPress}
								placeholder="Ask about your facilities, equipment, or maintenance..."
								disabled={isLoading}
								className="cc-input"
								size="large"
								suffix={
									<Button
										type="primary"
										icon={<SendOutlined />}
										onClick={() => handleSendMessage()}
										disabled={!inputValue.trim() || isLoading}
										className="cc-send-btn"
										shape="circle"
									/>
								}
							/>
						</div>
						<button
							type="button"
							className="cc-scan-action"
							onClick={() => setShowAssetUpload(true)}
						>
							<ScanOutlined className="cc-scan-action-icon" />
							<span>Scan Asset</span>
						</button>
						<button
							type="button"
							className="cc-scan-action cc-lens-action"
							onClick={() => handleNavigation("/facility-lens")}
						>
							<ScanOutlined className="cc-scan-action-icon" />
							<span>FacilityLens</span>
						</button>
					</div>
				</div>
			)}

			{/* Copyright bar */}
			<div className="cc-copyright">
				<Copyright />
			</div>

			{/* Soul Profile drawer */}
			<SoulProfile
				open={showSoulProfile}
				onClose={() => setShowSoulProfile(false)}
				onReset={handleSoulReset}
			/>

			{/* Scan Asset modal */}
			<AssetUpload
				open={showAssetUpload}
				onClose={() => setShowAssetUpload(false)}
				onResult={handleAssetUploadResult}
				onAddNewAsset={handleAddNewAsset}
			/>
		</div>
	);
};

export { CommandCenter };
